import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { ConfigProvider } from './ConfigContext';
import { useConfig } from './ConfigContextCore';

// Public Shared Components (Keep static for critical path)
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import ScrollToTop from './components/ScrollToTop';
import EventDisplay from './components/EventDisplay';

// Optimized Lazy Loading for Pages
const Home = lazy(() => import('./pages/Home/Home'));
const Contact = lazy(() => import('./pages/Contact/Contact'));
const CopyrightPolicy = lazy(() => import('./pages/Policies/CopyrightPolicy'));
const FAQPage = lazy(() => import('./pages/FAQs/FAQPage'));
const Disclaimer = lazy(() => import('./pages/Policies/Disclaimer'));
const RefundPolicy = lazy(() => import('./pages/Policies/RefundPolicy'));
const GOIPolicies = lazy(() => import('./pages/Policies/GOIPolicies'));
const TermsAndConditions = lazy(() => import('./pages/Policies/TermsAndConditions'));
const AboutVizag = lazy(() => import('./pages/About/AboutVizag'));
const AboutVSEZ = lazy(() => import('./pages/About/AboutVSEZ'));
const Administration = lazy(() => import('./pages/Administration/Administration'));
const WhoIsWho = lazy(() => import('./pages/Administration/WhoIsWho'));
const CircularsPage = lazy(() => import('./pages/InfoHub/CircularsPage'));
const CSTReimbursement = lazy(() => import('./pages/CSTDBK/CSTReimbursement'));
const DownloadsPage = lazy(() => import('./pages/Downloads/DownloadsPage'));
const EOUScheme = lazy(() => import('./pages/EOU/EOUScheme'));
const EOUUnits = lazy(() => import('./pages/EOU/EOUUnits'));
const EOUACMeetings = lazy(() => import('./pages/EOU/EOUACMeetings'));
const VsezUnits = lazy(() => import('./pages/SEZ/VsezUnits'));
const Feedback = lazy(() => import('./pages/Feedback/Feedback'));
const Grievances = lazy(() => import('./pages/RTI/Grievances'));
const RTIOfficers = lazy(() => import('./pages/RTI/RTIOfficers'));
const ParliamentQuestions = lazy(() => import('./pages/RTI/ParliamentQuestions'));
const QuarterlyReturns = lazy(() => import('./pages/RTI/QuarterlyReturns'));
const ReceiptsDisposal = lazy(() => import('./pages/RTI/ReceiptsDisposal'));
const Reports = lazy(() => import('./pages/RTI/Reports'));
const VoluntaryDisclosure = lazy(() => import('./pages/RTI/VoluntaryDisclosure'));
const HelpDesk = lazy(() => import('./pages/HelpDesk/HelpDesk'));
const Infrastructure = lazy(() => import('./pages/About/Infrastructure'));
const LocationMap = lazy(() => import('./pages/About/LocationMap'));
const WhatsNewPage = lazy(() => import('./pages/InfoHub/WhatsNewPage'));
const NodalOfficers = lazy(() => import('./pages/InfoHub/NodalOfficers'));
const Notifications = lazy(() => import('./pages/InfoHub/Notifications'));
const PhotoGallery = lazy(() => import('./pages/Gallery/PhotoGallery'));
const PressReleases = lazy(() => import('./pages/News/PressReleases'));
const NewsClips = lazy(() => import('./pages/News/NewsClips'));
const EnvironmentDay = lazy(() => import('./pages/News/EnvironmentDay'));
const Videos = lazy(() => import('./pages/News/Videos'));
const PrivateSEZ = lazy(() => import('./pages/SEZ/PrivateSEZ'));
const PrivateSEZUnits = lazy(() => import('./pages/SEZ/PrivateSEZUnits'));
const RelatedLinks = lazy(() => import('./pages/InfoHub/RelatedLinks'));
const SEZScheme = lazy(() => import('./pages/SEZ/SEZScheme'));
const SEZOnlineInstructions = lazy(() => import('./pages/SEZ/SEZOnlineInstructions'));
const SEZUACMeetings = lazy(() => import('./pages/SEZ/SEZUACMeetings'));
const StatisticalData = lazy(() => import('./pages/SEZ/StatisticalData'));
const SEZLocations = lazy(() => import('./pages/SEZ/SEZLocations'));
const Software = lazy(() => import('./pages/Software/Software'));
const SoftwareInfrastructure = lazy(() => import('./pages/Software/SoftwareInfrastructure'));
const ItSezsAp = lazy(() => import('./pages/Software/ItSezsAp'));
const SourceOfLand = lazy(() => import('./pages/InfoHub/SourceOfLand'));
const TendersNotice = lazy(() => import('./pages/Tenders/TendersNotice'));
const AwardedTenders = lazy(() => import('./pages/Tenders/AwardedTenders'));
const TSManufacturing = lazy(() => import('./pages/SEZ/TSManufacturing'));
const EOUStatisticalData = lazy(() => import('./pages/EOU/EOUStatisticalData'));
const WooCommerce = lazy(() => import('./pages/WooCommerce/WooCommerce'));
const DynamicPage = lazy(() => import('./pages/DynamicPage/DynamicPage'));
const Assets = lazy(() => import('./pages/Assets/Assets'));
const ScreenReaderAccess = lazy(() => import('./pages/Accessibility/ScreenReaderAccess'));
const Sitemap = lazy(() => import('./pages/Sitemap/Sitemap'));

// Admin Components (Separated bundle)
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const Login = lazy(() => import('./pages/Admin/Login'));

const PublicLayout = ({ children }) => {
  return (
    <div className="App">
      <EventDisplay type="banner" />
      <Header />
      <EventDisplay type="overlay" />
      <main>
        {children}
      </main>
      <Footer />
    </div>
  );
};

const AdminRoute = () => {
  const { isAdmin } = useConfig();
  return isAdmin ? <AdminDashboard /> : <Login />;
};

function App() {
  return (
    <ConfigProvider>
      <Router>
        <ScrollToTop />
        <Suspense fallback={<div className="p-20 text-center fw-bold text-primary">Loading Portal...</div>}>
          <Routes>

            {/* Public Routes */}
            <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
            <Route path="/copyright" element={<PublicLayout><CopyrightPolicy /></PublicLayout>} />
            <Route path="/disclaimer" element={<PublicLayout><Disclaimer /></PublicLayout>} />
            <Route path="/refund-returns" element={<PublicLayout><RefundPolicy /></PublicLayout>} />
            <Route path="/goi-policies" element={<PublicLayout><GOIPolicies /></PublicLayout>} />
            <Route path="/terms-and-conditions" element={<PublicLayout><TermsAndConditions /></PublicLayout>} />
            <Route path="/faqs" element={<PublicLayout><FAQPage /></PublicLayout>} />
            <Route path="/about-vizag" element={<PublicLayout><AboutVizag /></PublicLayout>} />
            <Route path="/about-vsez" element={<PublicLayout><AboutVSEZ /></PublicLayout>} />
            <Route path="/administration" element={<PublicLayout><Administration /></PublicLayout>} />
            <Route path="/who-is-who" element={<PublicLayout><WhoIsWho /></PublicLayout>} />
            <Route path="/circulars" element={<PublicLayout><CircularsPage /></PublicLayout>} />
            <Route path="/cst-status" element={<PublicLayout><CSTReimbursement /></PublicLayout>} />
            <Route path="/downloads" element={<PublicLayout><DownloadsPage /></PublicLayout>} />
            <Route path="/eou-scheme" element={<PublicLayout><EOUScheme /></PublicLayout>} />
            <Route path="/eou-units" element={<PublicLayout><EOUUnits /></PublicLayout>} />
            <Route path="/eou-ac-meetings" element={<PublicLayout><EOUACMeetings /></PublicLayout>} />
            <Route path="/feedback" element={<PublicLayout><Feedback /></PublicLayout>} />
            <Route path="/grievances" element={<PublicLayout><Grievances /></PublicLayout>} />
            <Route path="/rti-officers" element={<PublicLayout><RTIOfficers /></PublicLayout>} />
            <Route path="/parliament-questions" element={<PublicLayout><ParliamentQuestions /></PublicLayout>} />
            <Route path="/quarterly-returns" element={<PublicLayout><QuarterlyReturns /></PublicLayout>} />
            <Route path="/receipts-disposal" element={<PublicLayout><ReceiptsDisposal /></PublicLayout>} />
            <Route path="/reports" element={<PublicLayout><Reports /></PublicLayout>} />
            <Route path="/voluntary-disclosure" element={<PublicLayout><VoluntaryDisclosure /></PublicLayout>} />
            <Route path="/helpdesk" element={<PublicLayout><HelpDesk /></PublicLayout>} />
            <Route path="/infrastructure" element={<PublicLayout><Infrastructure /></PublicLayout>} />
            <Route path="/location-map" element={<PublicLayout><LocationMap /></PublicLayout>} />
            <Route path="/whats-new" element={<PublicLayout><WhatsNewPage /></PublicLayout>} />
            <Route path="/nodal-officers" element={<PublicLayout><NodalOfficers /></PublicLayout>} />
            <Route path="/notifications" element={<PublicLayout><Notifications /></PublicLayout>} />
            <Route path="/photo-gallery" element={<PublicLayout><PhotoGallery /></PublicLayout>} />
            <Route path="/press-releases" element={<PublicLayout><PressReleases /></PublicLayout>} />
            <Route path="/news-clips" element={<PublicLayout><NewsClips /></PublicLayout>} />
            <Route path="/environment-day" element={<PublicLayout><EnvironmentDay /></PublicLayout>} />
            <Route path="/videos" element={<PublicLayout><Videos /></PublicLayout>} />
            <Route path="/assets" element={<PublicLayout><Assets /></PublicLayout>} />
            <Route path="/vsez-units" element={<PublicLayout><VsezUnits /></PublicLayout>} />
            <Route path="/private-sez" element={<PublicLayout><PrivateSEZ /></PublicLayout>} />
            <Route path="/private-sez-units" element={<PublicLayout><PrivateSEZUnits /></PublicLayout>} />
            <Route path="/related-links" element={<PublicLayout><RelatedLinks /></PublicLayout>} />
            <Route path="/sez-scheme" element={<PublicLayout><SEZScheme /></PublicLayout>} />
            <Route path="/sez-online-instructions" element={<PublicLayout><SEZOnlineInstructions /></PublicLayout>} />
            <Route path="/sez-uac-meetings" element={<PublicLayout><SEZUACMeetings /></PublicLayout>} />
            <Route path="/statistical-data" element={<PublicLayout><StatisticalData /></PublicLayout>} />
            <Route path="/sez-locations" element={<PublicLayout><SEZLocations /></PublicLayout>} />
            <Route path="/software" element={<PublicLayout><Software /></PublicLayout>} />
            <Route path="/software-infrastructure" element={<PublicLayout><SoftwareInfrastructure /></PublicLayout>} />
            <Route path="/it-sezs-ap" element={<PublicLayout><ItSezsAp /></PublicLayout>} />
            <Route path="/source-of-land" element={<PublicLayout><SourceOfLand /></PublicLayout>} />
            <Route path="/tenders-notice" element={<PublicLayout><TendersNotice /></PublicLayout>} />
            <Route path="/awarded-tenders" element={<PublicLayout><AwardedTenders /></PublicLayout>} />
            <Route path="/ts-manufacturing" element={<PublicLayout><TSManufacturing /></PublicLayout>} />
            <Route path="/eou-statistical-data" element={<PublicLayout><EOUStatisticalData /></PublicLayout>} />
            <Route path="/woo-commerce" element={<PublicLayout><WooCommerce /></PublicLayout>} />
            <Route path="/screen-reader-access" element={<PublicLayout><ScreenReaderAccess /></PublicLayout>} />
            <Route path="/sitemap" element={<PublicLayout><Sitemap /></PublicLayout>} />
            <Route path="/p/:slug" element={<PublicLayout><DynamicPage /></PublicLayout>} />
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminRoute />} />
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Suspense>
      </Router>
    </ConfigProvider>
  );
}

export default App;